import { useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Send, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MediaUploader, MediaPreview } from "@/components/MediaUploader";
import { toast } from "sonner";

/**
 * PostComposer — Story / Live Drop input.
 * Text posts: all logged-in users.
 * Photo/video drops: Verified Witness + Research Council (+ admins).
 */
export function PostComposer({ onPosted, caseId = null, compact = false }) {
    const { user } = useAuth();
    const [body, setBody] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [posting, setPosting] = useState(false);
    const canMedia = user && (user.role === "admin" || ["verified_witness", "research_council"].includes(user.subscription_tier));

    if (!user) {
        return (
            <div className="ok-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="post-composer-signedout">
                <div className="text-sm opacity-80 flex items-center gap-2">
                    <Radio className="w-4 h-4 text-[var(--ok-accent)]" />
                    Sign in to drop a sighting, link, or quick finding to the live wall.
                </div>
                <Link to="/login" className="ok-btn-primary text-xs" data-testid="post-composer-login-btn">Log in</Link>
            </div>
        );
    }

    async function submit() {
        if (!body.trim() && !attachment) return;
        setPosting(true);
        try {
            const payload = { body: body.trim() };
            if (attachment) payload.media_id = attachment.id;
            if (caseId) payload.case_id = caseId;
            const { data } = await api.post("/social/posts", payload);
            setBody("");
            setAttachment(null);
            onPosted?.(data);
            toast.success("Posted to the live wall");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setPosting(false);
        }
    }

    return (
        <div className="ok-card p-4" data-testid="post-composer">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] flex items-center gap-2 mb-2">
                <Radio className="w-3 h-3" /> Live drop
            </div>
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={compact ? 2 : 3}
                maxLength={2000}
                placeholder="Drop a sighting, link, thought or quick finding…"
                className="ok-input w-full text-sm"
                data-testid="post-composer-textarea"
            />
            {attachment && (
                <div className="mt-2" data-testid="post-composer-attachment">
                    <MediaPreview media={attachment} onRemove={() => setAttachment(null)} />
                </div>
            )}
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                {canMedia ? (
                    <MediaUploader compact onUploaded={setAttachment} testId="post-attach" />
                ) : (
                    <Link to="/pricing" className="font-mono text-[10px] uppercase tracking-widest opacity-60 hover:opacity-100 inline-flex items-center gap-1" data-testid="post-media-locked">
                        <Lock className="w-3 h-3" /> Photo/video drops · Witness &amp; Council tiers
                    </Link>
                )}
                <button
                    onClick={submit}
                    disabled={posting || (!body.trim() && !attachment)}
                    className="ok-btn-primary text-sm inline-flex items-center gap-1"
                    data-testid="post-composer-submit"
                >
                    <Send className="w-3 h-3" /> {posting ? "Posting…" : "Post"}
                </button>
            </div>
        </div>
    );
}
